import { useState } from "react";
import type { AdminNotificationListItemResponse } from "../../../types/adminTypes";
import { formatDate } from "../../../utils/DateUtils";
import Button from "../../ui/Button/Button";
import AdminPillBadge from "../ui/AdminPillBadge";
import AdminNotificationDetailModal from "./AdminNotificationDetailModal";
import AdminNotificationsFilters from "./AdminNotificationsFilters";
import AdminNotificationsTableRow from "./AdminNotificationsTableRow";
import AdminTableShell from "../ui/AdminTableShell";
import { adminNotificationsColumns } from "./adminNotificationsColumns";

type AdminNotificationsTableProps = {
  notifications: AdminNotificationListItemResponse[];
  isLoading: boolean;
  canWrite: boolean;
  search: string;
  status: string;
  type: string;
  createdFromDate: string;
  createdToDate: string;
  pageSize: number;
  page: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onPageChange: (nextPage: number) => void;
};

const AdminNotificationsTable = ({
  notifications,
  isLoading,
  canWrite,
  search,
  status,
  type,
  createdFromDate,
  createdToDate,
  pageSize,
  page,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onStatusChange,
  onTypeChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onPageSizeChange,
  onPageChange,
}: AdminNotificationsTableProps) => {
  const [selectedNotification, setSelectedNotification] = useState<AdminNotificationListItemResponse | null>(null);

  const statusTone: Record<AdminNotificationListItemResponse["status"], "warning" | "success" | "danger"> = {
    PENDING: "warning",
    SENT: "success",
    FAILED: "danger",
  };

  return (
    <>
      <AdminTableShell
        columns={adminNotificationsColumns}
        filters={
          <AdminNotificationsFilters
            search={search}
            status={status}
            type={type}
            createdFromDate={createdFromDate}
            createdToDate={createdToDate}
            pageSize={pageSize}
            onSearchChange={onSearchChange}
            onStatusChange={onStatusChange}
            onTypeChange={onTypeChange}
            onCreatedFromDateChange={onCreatedFromDateChange}
            onCreatedToDateChange={onCreatedToDateChange}
            onPageSizeChange={onPageSizeChange}
          />
        }
        mobileCards={notifications.map((notification) => (
          <article key={notification.id} className="rounded-[1.5rem] border border-myteengig-border bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-myteengig-text-primary">{notification.recipientEmail}</p>
                <p className="mt-1 text-sm text-myteengig-text-muted">{notification.subject}</p>
              </div>
              <AdminPillBadge tone={statusTone[notification.status]}>{notification.status}</AdminPillBadge>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Type</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <AdminPillBadge>{notification.type}</AdminPillBadge>
                  <span className="text-sm text-myteengig-text-muted">{notification.channel}</span>
                </div>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Attempts</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{notification.attemptCount}</p>
                <p className="mt-1 text-xs text-myteengig-text-muted">{notification.lastError ? "Has error" : "No error"}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Next Run</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{formatDate(notification.availableAt)}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Created</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{formatDate(notification.createdAt)}</p>
              </div>
              {notification.lastError ? (
                <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3 sm:col-span-2">
                  <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Last error</p>
                  <p className="mt-1 text-sm text-myteengig-text-primary">{notification.lastError}</p>
                </div>
              ) : null}
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setSelectedNotification(notification)}>
                View
              </Button>
            </div>
          </article>
        ))}
        rows={notifications.map((notification) => (
          <AdminNotificationsTableRow key={notification.id} notification={notification} onView={setSelectedNotification} />
        ))}
        isLoading={isLoading}
        isEmpty={!isLoading && notifications.length === 0}
        emptyMessage="No notifications found for the current filters."
        page={page}
        pageSize={pageSize}
        resultsCount={resultsCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={onPageChange}
      />
      <AdminNotificationDetailModal
        notification={selectedNotification}
        isOpen={selectedNotification !== null}
        canWrite={canWrite}
        onClose={() => setSelectedNotification(null)}
      />
    </>
  );
};

export default AdminNotificationsTable;
